import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Layout from '../../shared/components/Layout';
import Modal from '../../shared/components/Modal';
import DecimalAmountWithUsd from '../../shared/components/DecimalAmountWithUsd';
import MultiSelect, { MultiSelectOption } from '../../shared/components/MultiSelect';
import Pagination from '../../shared/components/Pagination';
import {
  P2pAppliesTo,
  P2pFeeType,
  P2pOfferType,
  P2pRate,
  P2pRatePreviewResult,
  PaginatedResponse,
} from '../../shared/types';
import {
  CRYPTO_CURRENCIES,
  COUNTRIES,
  FIAT_CURRENCIES,
} from '../../shared/constants/markets';
import {
  RateInput,
  RatePreviewInput,
  createRate,
  deleteRate,
  listRates,
  previewRate,
  updateRate,
} from './ratesApi';
import { listPaymentMethods } from './paymentMethodsApi';
import { useAuth } from '../auth/AuthContext';

const FEE_TYPE_OPTIONS: { value: P2pFeeType; label: string; hint: string }[] = [
  { value: 'percentage', label: 'Percentage', hint: '% of the crypto amount traded' },
  { value: 'fixed', label: 'Fixed', hint: 'flat amount in the traded crypto' },
  { value: 'maker_taker', label: 'Maker / Taker', hint: 'separate % for maker and taker' },
  { value: 'spread', label: 'Spread', hint: '% spread baked into displayed price' },
];

const APPLIES_OPTIONS: { value: P2pAppliesTo; label: string }[] = [
  { value: 'maker', label: 'Maker' },
  { value: 'taker', label: 'Taker' },
  { value: 'both', label: 'Both' },
  { value: 'none', label: 'None (informational)' },
];

const OFFER_TYPE_OPTIONS: { value: '' | P2pOfferType; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'buy', label: 'Buy' },
  { value: 'sell', label: 'Sell' },
];

const FEE_TYPE_BADGE: Record<P2pFeeType, string> = {
  percentage: 'badge-info',
  fixed: 'badge-warning',
  maker_taker: 'badge-success',
  spread: 'badge-danger',
};

const FEE_TYPE_LABEL: Record<P2pFeeType, string> = {
  percentage: 'Percentage',
  fixed: 'Fixed',
  maker_taker: 'Maker/Taker',
  spread: 'Spread',
};

const APPLIES_LABEL: Record<P2pAppliesTo, string> = {
  maker: 'Maker',
  taker: 'Taker',
  both: 'Both',
  none: 'None',
};

const emptyForm: RateInput = {
  name: '',
  description: '',
  fee_type: 'percentage',
  applies_to: 'taker',
  value: '',
  maker_value: '',
  taker_value: '',
  min_fee: '',
  max_fee: '',
  currencies: [],
  fiat_currencies: [],
  countries: [],
  payment_method_ids: [],
  offer_type: null,
  min_verification_level: 0,
  is_active: true,
  effective_from: '',
  effective_to: '',
};

const CRYPTO_OPTIONS: MultiSelectOption[] = CRYPTO_CURRENCIES;
const FIAT_OPTIONS: MultiSelectOption[] = FIAT_CURRENCIES;
const COUNTRY_OPTIONS: MultiSelectOption[] = COUNTRIES;

const emptyPreview: RatePreviewInput = {
  currency: '',
  fiat_currency: '',
  country: '',
  offer_type: undefined,
  verification_level: 0,
  total: '',
};

const joinList = (values: string[] | null | undefined): string | null => {
  if (!values || values.length === 0) return null;
  if (values.length <= 3) return values.join('/');
  return `${values.slice(0, 3).join('/')}+${values.length - 3}`;
};

const formatScope = (rate: P2pRate): string => {
  const parts: string[] = [];
  const crypto = joinList(rate.currencies);
  const fiat = joinList(rate.fiat_currencies);
  const countries = joinList(rate.countries);
  if (crypto) parts.push(crypto);
  if (fiat) parts.push(fiat);
  if (countries) parts.push(countries);
  if (rate.offer_type) parts.push(rate.offer_type.toUpperCase());
  if (rate.payment_method_ids && rate.payment_method_ids.length > 0) {
    parts.push(`PM×${rate.payment_method_ids.length}`);
  }
  if (rate.min_verification_level > 0) parts.push(`KYC≥${rate.min_verification_level}`);
  return parts.length > 0 ? parts.join(' · ') : 'Global';
};

const formatValue = (rate: P2pRate): string => {
  if (rate.fee_type === 'maker_taker') {
    const m = rate.maker_value ?? '0';
    const t = rate.taker_value ?? '0';
    return `M ${m}% / T ${t}%`;
  }
  if (rate.fee_type === 'fixed') {
    return `${rate.value} (crypto)`;
  }
  return `${rate.value}%`;
};

// Fee schedules are kept per section: P2P and OTC (cash in person) each
// have their own list, and a rate only ever applies to its own section.
type Props = { mode: 'p2p' | 'otc' };

const Rates: React.FC<Props> = ({ mode }) => {
  const isOtc = mode === 'otc';
  const { admin } = useAuth();
  const [rates, setRates] = useState<PaginatedResponse<P2pRate> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [feeTypeFilter, setFeeTypeFilter] = useState<'' | P2pFeeType>('');
  const [activeFilter, setActiveFilter] = useState<'' | 'true' | 'false'>('');

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<P2pRate | null>(null);
  const [formData, setFormData] = useState<RateInput>(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionError, setActionError] = useState('');

  const [previewInput, setPreviewInput] = useState<RatePreviewInput>(emptyPreview);
  const [previewResult, setPreviewResult] = useState<P2pRatePreviewResult | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');

  const [paymentMethodOptions, setPaymentMethodOptions] = useState<MultiSelectOption[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await listPaymentMethods({ page: 1, per_page: 200 });
        if (cancelled) return;
        setPaymentMethodOptions(
          data.data.map((pm) => ({ value: String(pm.id), label: pm.name }))
        );
      } catch {
        // Non-fatal: the payment method multiselect will simply be empty.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const canManage = useMemo(() => {
    if (!admin) return false;
    if (admin.role === 'super_admin') return true;
    if (admin.backoffice_role === 'administrator') return true;
    return admin.permissions?.includes('p2p.manage_rates') ?? false;
  }, [admin]);

  const load = useCallback(async (page: number) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await listRates({
        page,
        per_page: 20,
        mode,
        search: search || undefined,
        fee_type: feeTypeFilter || undefined,
        is_active: activeFilter === '' ? undefined : activeFilter === 'true',
      });
      setRates(data);
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { error?: string } } }).response?.data?.error ?? 'Failed to load rates'
        : 'Failed to load rates';
      setError(msg);
      setRates(null);
    } finally {
      setIsLoading(false);
    }
  }, [search, feeTypeFilter, activeFilter, mode]);

  useEffect(() => {
    load(currentPage);
  }, [currentPage, load]);

  const applyFilters = () => {
    setSearch(searchInput.trim());
    setCurrentPage(1);
  };

  const openCreate = () => {
    setEditing(null);
    setFormData(emptyForm);
    setActionError('');
    setModalOpen(true);
  };

  const openEdit = (rate: P2pRate) => {
    setEditing(rate);
    setFormData({
      name: rate.name,
      description: rate.description ?? '',
      fee_type: rate.fee_type,
      applies_to: rate.applies_to,
      value: rate.value ?? '',
      maker_value: rate.maker_value ?? '',
      taker_value: rate.taker_value ?? '',
      min_fee: rate.min_fee ?? '',
      max_fee: rate.max_fee ?? '',
      currencies: rate.currencies ?? [],
      fiat_currencies: rate.fiat_currencies ?? [],
      countries: rate.countries ?? [],
      payment_method_ids: rate.payment_method_ids ?? [],
      offer_type: rate.offer_type,
      min_verification_level: rate.min_verification_level,
      is_active: rate.is_active,
      effective_from: rate.effective_from ? rate.effective_from.slice(0, 16) : '',
      effective_to: rate.effective_to ? rate.effective_to.slice(0, 16) : '',
    });
    setActionError('');
    setModalOpen(true);
  };

  const buildPayload = (): RateInput => {
    const cleanString = (v: unknown): string | null => {
      if (v === null || v === undefined) return null;
      const s = String(v).trim();
      return s === '' ? null : s;
    };
    const cleanNumber = (v: unknown): number | string | null => {
      if (v === null || v === undefined) return null;
      const s = String(v).trim();
      return s === '' ? null : s;
    };

    const payload: RateInput = {
      mode,
      name: formData.name.trim(),
      description: cleanString(formData.description),
      fee_type: formData.fee_type,
      applies_to: formData.applies_to,
      value: cleanNumber(formData.value) ?? 0,
      maker_value: cleanNumber(formData.maker_value),
      taker_value: cleanNumber(formData.taker_value),
      min_fee: cleanNumber(formData.min_fee),
      max_fee: cleanNumber(formData.max_fee),
      currencies: formData.currencies ?? [],
      fiat_currencies: formData.fiat_currencies ?? [],
      countries: formData.countries ?? [],
      payment_method_ids: formData.payment_method_ids ?? [],
      offer_type: formData.offer_type ?? null,
      min_verification_level: Number(formData.min_verification_level ?? 0),
      is_active: formData.is_active ?? true,
      effective_from: cleanString(formData.effective_from),
      effective_to: cleanString(formData.effective_to),
    };
    return payload;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setActionError('');
    setIsSubmitting(true);
    try {
      const payload = buildPayload();
      if (editing) {
        await updateRate(editing.id, payload);
      } else {
        await createRate(payload);
      }
      setModalOpen(false);
      setEditing(null);
      load(currentPage);
    } catch (err: unknown) {
      const resp = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { error?: string; message?: string; errors?: Record<string, string[]> } } }).response
        : undefined;
      const errors = resp?.data?.errors;
      if (errors) {
        const first = Object.values(errors)[0];
        setActionError(Array.isArray(first) ? first[0] : 'Validation failed');
      } else {
        setActionError(resp?.data?.error || resp?.data?.message || 'Failed to save');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (rate: P2pRate) => {
    if (!window.confirm(`Delete rate "${rate.name}"?`)) return;
    try {
      await deleteRate(rate.id);
      load(currentPage);
    } catch {
      window.alert('Failed to delete');
    }
  };

  const handleToggleActive = async (rate: P2pRate) => {
    try {
      await updateRate(rate.id, {
        name: rate.name,
        fee_type: rate.fee_type,
        applies_to: rate.applies_to,
        is_active: !rate.is_active,
      });
      load(currentPage);
    } catch {
      window.alert('Failed to update status');
    }
  };

  const runPreview = async () => {
    setPreviewError('');
    setPreviewLoading(true);
    try {
      const cleaned: RatePreviewInput = {
        mode,
        currency: previewInput.currency?.trim().toUpperCase() || undefined,
        fiat_currency: previewInput.fiat_currency?.trim().toUpperCase() || undefined,
        country: previewInput.country?.trim().toUpperCase() || undefined,
        offer_type: previewInput.offer_type || undefined,
        verification_level: Number(previewInput.verification_level ?? 0) || 0,
        total: previewInput.total !== '' && previewInput.total !== undefined
          ? Number(previewInput.total)
          : undefined,
      };
      const result = await previewRate(cleaned);
      setPreviewResult(result);
    } catch (err: unknown) {
      const resp = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { message?: string; error?: string } } }).response
        : undefined;
      setPreviewError(resp?.data?.message || resp?.data?.error || 'Preview failed');
      setPreviewResult(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const showMakerTakerInputs = formData.fee_type === 'maker_taker';
  const showValueInput = formData.fee_type !== 'maker_taker';

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">{isOtc ? 'OTC' : 'P2P'} Fee Rates</h1>
            <p className="text-slate-400 mt-1">
              {isOtc ? 'Fee schedules for OTC cash trades — separate from P2P. ' : 'Platform fee schedules. '}
              The most specific active rate wins at trade time.
              Supports percentage, fixed, maker/taker and spread fee types, scoped by
              currency, fiat, country{isOtc ? '' : ', payment method'} and KYC level.
            </p>
          </div>
          {canManage && (
            <button onClick={openCreate} className="btn btn-primary">
              + Add Rate
            </button>
          )}
        </div>

        <div className="card p-4 flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Search</label>
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), applyFilters())}
              placeholder="Name or description"
              className="input py-2 w-64"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Fee type</label>
            <select
              value={feeTypeFilter}
              onChange={(e) => { setFeeTypeFilter(e.target.value as '' | P2pFeeType); setCurrentPage(1); }}
              className="input py-2"
            >
              <option value="">All types</option>
              {FEE_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Status</label>
            <select
              value={activeFilter}
              onChange={(e) => { setActiveFilter(e.target.value as '' | 'true' | 'false'); setCurrentPage(1); }}
              className="input py-2"
            >
              <option value="">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
          <button type="button" onClick={applyFilters} className="btn btn-secondary py-2">Apply</button>
          {(search || feeTypeFilter || activeFilter) && (
            <button
              type="button"
              onClick={() => { setSearchInput(''); setSearch(''); setFeeTypeFilter(''); setActiveFilter(''); setCurrentPage(1); }}
              className="btn btn-secondary py-2"
            >
              Clear
            </button>
          )}
        </div>

        <div className="card overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-12 h-12 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="p-6 text-center">
              <p className="text-red-400">{error}</p>
            </div>
          ) : !rates?.data.length ? (
            <div className="p-12 text-center text-slate-400">
              No rates configured. {canManage && 'Click "+ Add Rate" to create one.'}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-800/50">
                    <tr>
                      <th className="table-header">ID</th>
                      <th className="table-header">Name</th>
                      <th className="table-header">Type</th>
                      <th className="table-header">Applies to</th>
                      <th className="table-header">Value</th>
                      <th className="table-header">Scope</th>
                      <th className="table-header">Status</th>
                      <th className="table-header">Updated</th>
                      {canManage && <th className="table-header text-right">Actions</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {rates.data.map((r) => (
                      <tr key={r.id} className="hover:bg-slate-800/30 transition-colors">
                        <td className="table-cell font-mono text-indigo-400">#{r.id}</td>
                        <td className="table-cell text-white">
                          <div className="font-medium">{r.name}</div>
                          {r.description && (
                            <div className="text-xs text-slate-500 max-w-xs truncate">{r.description}</div>
                          )}
                        </td>
                        <td className="table-cell">
                          <span className={`badge ${FEE_TYPE_BADGE[r.fee_type]}`}>
                            {FEE_TYPE_LABEL[r.fee_type]}
                          </span>
                        </td>
                        <td className="table-cell text-slate-300">{APPLIES_LABEL[r.applies_to]}</td>
                        <td className="table-cell font-mono text-slate-200">{formatValue(r)}</td>
                        <td className="table-cell text-slate-400 text-xs">{formatScope(r)}</td>
                        <td className="table-cell">
                          <span className={`badge ${r.is_active ? 'badge-success' : 'badge-warning'}`}>
                            {r.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="table-cell text-slate-400 text-xs">
                          {new Date(r.updated_at).toLocaleString()}
                        </td>
                        {canManage && (
                          <td className="table-cell text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => handleToggleActive(r)}
                                className="px-3 py-1.5 text-xs font-medium text-slate-300 hover:text-white bg-slate-700/40 hover:bg-slate-700 rounded-lg transition-all"
                              >
                                {r.is_active ? 'Deactivate' : 'Activate'}
                              </button>
                              <button
                                onClick={() => openEdit(r)}
                                className="px-3 py-1.5 text-xs font-medium text-indigo-400 hover:text-indigo-300 bg-indigo-500/10 hover:bg-indigo-500/20 rounded-lg transition-all"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDelete(r)}
                                className="px-3 py-1.5 text-xs font-medium text-red-400 hover:text-red-300 bg-red-500/10 hover:bg-red-500/20 rounded-lg transition-all"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {rates.last_page > 1 && (
                <div className="px-6 py-4 border-t border-slate-800">
                  <Pagination
                    currentPage={currentPage}
                    lastPage={rates.last_page}
                    onPageChange={setCurrentPage}
                  />
                </div>
              )}
            </>
          )}
        </div>

        {canManage && (
          <div className="card p-5">
            <h2 className="text-lg font-semibold text-white mb-2">Fee preview</h2>
            <p className="text-slate-400 text-sm mb-4">
              Simulate a trade context to verify which rate would apply and how much fee would be charged.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1">Currency</label>
                <select
                  className="input py-2"
                  value={previewInput.currency ?? ''}
                  onChange={(e) => setPreviewInput((p) => ({ ...p, currency: e.target.value || undefined }))}
                >
                  <option value="">Any</option>
                  {CRYPTO_CURRENCIES.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1">Fiat</label>
                <select
                  className="input py-2"
                  value={previewInput.fiat_currency ?? ''}
                  onChange={(e) => setPreviewInput((p) => ({ ...p, fiat_currency: e.target.value || undefined }))}
                >
                  <option value="">Any</option>
                  {FIAT_CURRENCIES.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1">Country</label>
                <select
                  className="input py-2"
                  value={previewInput.country ?? ''}
                  onChange={(e) => setPreviewInput((p) => ({ ...p, country: e.target.value || undefined }))}
                >
                  <option value="">Any</option>
                  {COUNTRIES.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1">Offer type</label>
                <select
                  className="input py-2"
                  value={previewInput.offer_type ?? ''}
                  onChange={(e) => setPreviewInput((p) => ({ ...p, offer_type: (e.target.value || undefined) as P2pOfferType | undefined }))}
                >
                  {OFFER_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1">KYC level</label>
                <input
                  type="number"
                  min={0}
                  max={10}
                  className="input py-2"
                  value={previewInput.verification_level ?? 0}
                  onChange={(e) => setPreviewInput((p) => ({ ...p, verification_level: Number(e.target.value) }))}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1">
                  Total (currency{previewInput.currency ? ` ${previewInput.currency}` : ''})
                </label>
                <input
                  type="number"
                  min={0}
                  step="any"
                  className="input py-2"
                  value={previewInput.total as string | number ?? ''}
                  onChange={(e) => setPreviewInput((p) => ({ ...p, total: e.target.value }))}
                  placeholder="100"
                />
              </div>
            </div>
            <div className="mt-4 flex items-center gap-3">
              <button
                type="button"
                onClick={runPreview}
                disabled={previewLoading}
                className="btn btn-primary disabled:opacity-50"
              >
                {previewLoading ? 'Computing...' : 'Compute fee'}
              </button>
              {previewError && <span className="text-sm text-red-400">{previewError}</span>}
            </div>

            {previewResult && (
              <div className="mt-5 p-4 bg-slate-800/40 border border-slate-700/60 rounded-lg">
                {previewResult.rate ? (
                  <div className="flex flex-wrap items-start gap-4">
                    <div className="flex-1 min-w-[200px]">
                      <div className="text-xs uppercase tracking-wider text-slate-500">Matched rate</div>
                      <div className="text-white font-medium">{previewResult.rate.name}</div>
                      <div className="text-xs text-slate-400 mt-1">
                        {FEE_TYPE_LABEL[previewResult.rate.fee_type]} · {APPLIES_LABEL[previewResult.rate.applies_to]}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">{formatScope(previewResult.rate)}</div>
                    </div>
                    <div className="grid grid-cols-3 gap-4 flex-1 min-w-[280px]">
                      <div>
                        <div className="text-xs text-slate-500">Maker fee</div>
                        <div className="text-emerald-300 font-mono">
                          {previewResult.fee.fee_currency ? (
                            <DecimalAmountWithUsd
                              amount={previewResult.fee.maker_fee}
                              currency={previewResult.fee.fee_currency}
                            />
                          ) : (
                            previewResult.fee.maker_fee
                          )}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">Taker fee</div>
                        <div className="text-emerald-300 font-mono">
                          {previewResult.fee.fee_currency ? (
                            <DecimalAmountWithUsd
                              amount={previewResult.fee.taker_fee}
                              currency={previewResult.fee.fee_currency}
                            />
                          ) : (
                            previewResult.fee.taker_fee
                          )}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">Total fee</div>
                        <div className="text-emerald-200 font-mono font-semibold">
                          {previewResult.fee.fee_currency ? (
                            <DecimalAmountWithUsd
                              amount={previewResult.fee.total_fee}
                              currency={previewResult.fee.fee_currency}
                            />
                          ) : (
                            <>
                              {previewResult.fee.total_fee}
                              {previewResult.fee.fee_currency && (
                                <span className="text-slate-500 ml-1 text-xs">
                                  {previewResult.fee.fee_currency}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    {previewResult.rate.fee_type === 'spread' && (
                      <div className="w-full">
                        <div className="text-xs text-slate-500">Spread</div>
                        <div className="text-amber-300 font-mono">{previewResult.fee.spread}%</div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-slate-400 text-sm">
                    No active rate matches this context. Trades will be processed with zero platform fee.
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <Modal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setEditing(null); }}
        title={editing ? `Edit rate #${editing.id}` : 'Add rate'}
        size="2xl"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {actionError && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {actionError}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-slate-300 mb-2">Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                className="input"
                placeholder="e.g. Default USDT/RUB taker fee"
                required
                maxLength={191}
              />
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-slate-300 mb-2">Description</label>
              <textarea
                value={formData.description ?? ''}
                onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
                className="input min-h-[60px]"
                maxLength={2000}
                rows={2}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Fee type *</label>
              <select
                value={formData.fee_type}
                onChange={(e) => setFormData((p) => ({ ...p, fee_type: e.target.value as P2pFeeType }))}
                className="input"
                required
              >
                {FEE_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label} — {o.hint}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Applies to *</label>
              <select
                value={formData.applies_to}
                onChange={(e) => setFormData((p) => ({ ...p, applies_to: e.target.value as P2pAppliesTo }))}
                className="input"
                required
              >
                {APPLIES_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            {showValueInput && (
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Value {formData.fee_type === 'fixed' ? '(amount in traded crypto)' : '(%)'}
                </label>
                <input
                  type="number"
                  step="0.0001"
                  min={0}
                  value={formData.value ?? ''}
                  onChange={(e) => setFormData((p) => ({ ...p, value: e.target.value }))}
                  className="input"
                  placeholder={formData.fee_type === 'spread' ? '0.30' : '0.50'}
                />
              </div>
            )}

            {showMakerTakerInputs && (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Maker value (%)</label>
                  <input
                    type="number"
                    step="0.0001"
                    min={0}
                    value={formData.maker_value ?? ''}
                    onChange={(e) => setFormData((p) => ({ ...p, maker_value: e.target.value }))}
                    className="input"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Taker value (%)</label>
                  <input
                    type="number"
                    step="0.0001"
                    min={0}
                    value={formData.taker_value ?? ''}
                    onChange={(e) => setFormData((p) => ({ ...p, taker_value: e.target.value }))}
                    className="input"
                    placeholder="0.20"
                  />
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Min fee (in traded crypto)</label>
              <input
                type="number"
                step="0.0001"
                min={0}
                value={formData.min_fee ?? ''}
                onChange={(e) => setFormData((p) => ({ ...p, min_fee: e.target.value }))}
                className="input"
                placeholder="optional"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Max fee (in traded crypto)</label>
              <input
                type="number"
                step="0.0001"
                min={0}
                value={formData.max_fee ?? ''}
                onChange={(e) => setFormData((p) => ({ ...p, max_fee: e.target.value }))}
                className="input"
                placeholder="optional"
              />
            </div>

            <div className="sm:col-span-2 mt-2 pt-2 border-t border-slate-800/60">
              <p className="text-xs uppercase tracking-wider text-slate-500 mb-2">Scope (leave empty = applies to all)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Crypto currency</label>
              <MultiSelect
                options={CRYPTO_OPTIONS}
                value={formData.currencies ?? []}
                onChange={(vals) => setFormData((p) => ({ ...p, currencies: vals }))}
                placeholder="All crypto"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Fiat currency</label>
              <MultiSelect
                options={FIAT_OPTIONS}
                value={formData.fiat_currencies ?? []}
                onChange={(vals) => setFormData((p) => ({ ...p, fiat_currencies: vals }))}
                placeholder="All fiat"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Country</label>
              <MultiSelect
                options={COUNTRY_OPTIONS}
                value={formData.countries ?? []}
                onChange={(vals) => setFormData((p) => ({ ...p, countries: vals }))}
                placeholder="All countries"
              />
            </div>

            {!isOtc && (
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Payment methods</label>
              <MultiSelect
                options={paymentMethodOptions}
                value={(formData.payment_method_ids ?? []).map(String)}
                onChange={(vals) =>
                  setFormData((p) => ({
                    ...p,
                    payment_method_ids: vals.map((v) => Number(v)).filter((n) => Number.isFinite(n)),
                  }))
                }
                placeholder="All payment methods"
              />
            </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Offer type</label>
              <select
                value={formData.offer_type ?? ''}
                onChange={(e) => setFormData((p) => ({
                  ...p,
                  offer_type: (e.target.value || null) as P2pOfferType | null,
                }))}
                className="input"
              >
                <option value="">Both</option>
                <option value="buy">Buy only</option>
                <option value="sell">Sell only</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Minimum verification level</label>
              <input
                type="number"
                min={0}
                max={10}
                value={formData.min_verification_level ?? 0}
                onChange={(e) => setFormData((p) => ({ ...p, min_verification_level: Number(e.target.value) }))}
                className="input"
              />
            </div>

            <div className="sm:col-span-2 mt-2 pt-2 border-t border-slate-800/60">
              <p className="text-xs uppercase tracking-wider text-slate-500 mb-2">Validity</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Status</label>
              <select
                value={formData.is_active ? '1' : '0'}
                onChange={(e) => setFormData((p) => ({ ...p, is_active: e.target.value === '1' }))}
                className="input"
              >
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Effective from</label>
              <input
                type="datetime-local"
                value={formData.effective_from ?? ''}
                onChange={(e) => setFormData((p) => ({ ...p, effective_from: e.target.value }))}
                className="input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Effective to</label>
              <input
                type="datetime-local"
                value={formData.effective_to ?? ''}
                onChange={(e) => setFormData((p) => ({ ...p, effective_to: e.target.value }))}
                className="input"
              />
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t border-slate-800/60">
            <button
              type="button"
              onClick={() => { setModalOpen(false); setEditing(null); }}
              className="flex-1 btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !formData.name.trim()}
              className="flex-1 btn btn-primary disabled:opacity-50"
            >
              {isSubmitting ? 'Saving...' : editing ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>
    </Layout>
  );
};

export default Rates;
